import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  input,
  output,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { PreviewDocumentThumbnailComponent } from './preview-document-thumbnail/preview-document-thumbnail.component';
import { PreviewPageThumbnailComponent } from './preview-page-thumbnail/preview-page-thumbnail.component';
import { NgClass } from '@angular/common';
import { DocumentFilterComponent } from './document-class-filter/document-filter.component';
import { PSDocument } from '../model/document.interface';
import { BehandlerItem } from '../model/behandler-item.interface';

@Component({
  selector: 'shared-dp-preview-thumbnail-bar',
  imports: [
    PreviewDocumentThumbnailComponent,
    PreviewPageThumbnailComponent,
    NgClass,
    DocumentFilterComponent,
  ],
  templateUrl: './preview-thumbnail-bar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewThumbnailBarComponent {
  readonly currentPageIndex = input<number>(0);

  readonly documentClassFilterEnabled = input(false);
  readonly availableDocumentClasses = input.required<string[]>();
  readonly availableBehandlers = input.required<BehandlerItem[]>();
  readonly selectedDocumentId = input.required<string>();

  readonly documents = input.required<PSDocument[]>();
  readonly singleOpenedDocument = input<PSDocument>();
  readonly documentLabelTemplate = input<TemplateRef<{ $implicit: PSDocument }>>();
  readonly documentThumbnailOverlayTemplate = input<TemplateRef<{ $implicit: PSDocument }>>();

  readonly isSingleDocumentView = input.required<boolean>();

  readonly isPreviewListExpandedOrHovered = input.required<boolean>();

  readonly thumbnailPageSelected = output<string>();
  readonly documentThumbnailSelected = output<string>();
  readonly appliedDocumentClassesChanged = output<string[]>();
  readonly appliedBehandlersChanged = output<BehandlerItem[]>();

  protected readonly pagePreviewList =
    viewChild.required<ElementRef<HTMLDivElement>>('pagePreviewList');

  private scrollToChildAtIndex(index: number) {
    if (!this.pagePreviewList()) return;

    const child = this.pagePreviewList().nativeElement.children[index];
    if (!child) {
      console.warn(
        `Could not scroll to child at index ${index} because it's undefined. PagePreviewList children length: ${this.pagePreviewList().nativeElement.children.length}`,
      );
      return;
    }

    child.scrollIntoView({
      behavior: 'instant',
      block: 'center',
    });
  }

  constructor() {
    effect(() => {
      this.scrollToChildAtIndex(
        this.documents().findIndex((document) => document.id === this.selectedDocumentId()),
      );
    });

    effect(() => {
      if (this.isSingleDocumentView()) {
        this.scrollToChildAtIndex(this.currentPageIndex());
      }
    });
  }

  protected onAppliedDocumentClassesChanged(selectedDocumentClasses: string[]) {
    this.appliedDocumentClassesChanged.emit(selectedDocumentClasses);
  }

  protected onAppliedBehandlersChanged(selectedBehandlers: BehandlerItem[]) {
    this.appliedBehandlersChanged.emit(selectedBehandlers);
  }

  protected scrollToFirstPageOfDocument(documentIndex: number) {
    const document = this.documents()[documentIndex];
    this.selectDocumentPage(document.pages[0].id);
    this.documentThumbnailSelected.emit(document.id);
  }

  protected selectDocumentPage(pageId: string) {
    this.thumbnailPageSelected.emit(pageId);
  }
}
